import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import "../styles/default.scss";
import useHorizontalTableScrollState from "../hooks/useHorizontalTableScrollState";
import useStickyTableHeader from "../hooks/useStickyTableHeader";

const isReactElement = node =>
  node && typeof node === "object" && node.$$typeof != null;

const parseHeadingCell = element => {
  if (
    !isReactElement(element) &&
    element &&
    typeof element === "object" &&
    Object.prototype.hasOwnProperty.call(element, "content")
  ) {
    return {
      content: element.content,
      className: element.className,
      style: element.style
    };
  }
  return {
    content: element,
    className: undefined,
    style: undefined
  };
};

const renderHeadingRow = heading => (
  <tr>
    {heading.map((element, key) => {
      const {
        content,
        className: thClass,
        style: thStyle
      } = parseHeadingCell(element);
      return (
        <th
          key={key}
          className={thClass || undefined}
          style={thStyle || undefined}
        >
          {content}
        </th>
      );
    })}
  </tr>
);

const SquaberTable = ({ className, heading, rows, withDistinctRows }) => {
  const { tableRef, wrapperRef, scrollState, updateScrollState } =
    useHorizontalTableScrollState(heading, rows);
  const { hasOverflow, atStart, atEnd } = scrollState;

  const {
    theadRef,
    stickyHeaderRef,
    stickyTableRef,
    stickyVisible,
    updateStickyHeader
  } = useStickyTableHeader({ tableRef, wrapperRef, heading, rows });

  const handleScroll = () => {
    updateScrollState();
    updateStickyHeader();
  };

  return (
    <div
      className={classNames("table-parent", {
        "horizontal-scroll": hasOverflow,
        "at-scroll-start": atStart,
        "at-scroll-end": atEnd
      })}
    >
      {heading ? (
        <div
          className={classNames("sticky-table-header", {
            "is-visible": stickyVisible
          })}
          ref={stickyHeaderRef}
          aria-hidden={!stickyVisible}
        >
          <div className="table-wrapper">
            <table
              className={classNames("squaber-table", className)}
              ref={stickyTableRef}
            >
              <thead>{renderHeadingRow(heading)}</thead>
            </table>
          </div>
        </div>
      ) : null}
      <div
        className={classNames("table-wrapper", {
          horizontalScroll: hasOverflow
        })}
        ref={wrapperRef}
        onScroll={handleScroll}
      >
        <table
          className={classNames("squaber-table", className, {
            "with-distinct-rows": withDistinctRows,
            overflow: hasOverflow
          })}
          ref={tableRef}
        >
          {heading ? (
            <thead ref={theadRef}>{renderHeadingRow(heading)}</thead>
          ) : null}
          <tbody>
            {rows.map((Row, rowKey) =>
              Array.isArray(Row) ? (
                <tr key={rowKey}>
                  {Row.map((cell, cellKey) => {
                    const isCellObject =
                      cell !== null &&
                      typeof cell === "object" &&
                      !!cell.content;

                    let Element = "td";

                    if (isCellObject && cell && cell.isHeading) {
                      Element = "th";
                    }

                    return (
                      <Element
                        key={cellKey}
                        className={
                          isCellObject && cell.className ? cell.className : null
                        }
                        style={
                          isCellObject &&
                          cell.style &&
                          typeof cell.style === "object"
                            ? cell.style
                            : undefined
                        }
                        colSpan={
                          isCellObject && cell && cell.colspan
                            ? cell.colspan
                            : null
                        }
                      >
                        {isCellObject ? cell.content : cell}
                      </Element>
                    );
                  })}
                </tr>
              ) : (
                Row
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

SquaberTable.propTypes = {
  heading: PropTypes.array,
  withDistinctRows: PropTypes.bool,
  rows: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.arrayOf(
        PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.node,
          PropTypes.shape({
            isHeading: PropTypes.bool,
            content: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
              .isRequired,
            className: PropTypes.string,
            style: PropTypes.object
          })
        ])
      )
    ])
  ).isRequired
};

export default SquaberTable;
